import styles from "./filters.module.css";

import { useState, useMemo, useEffect } from "react";
import {
  Typography,
  Flex,
  Button,
  Descriptions,
  Tooltip,
  Collapse,
  Divider,
  Space,
  Col,
  Row,
} from "antd";

import FilterNumber from "@/components/inputs/FilterNumber";
import FilterMoney from "@/components/inputs/FilterMoney";
import FilterString from "@/components/inputs/FilterString";
import FilterCalendar from "@/components/inputs/FilterCalendar";
import FilterDefinition from "@/components/inputs/FilterDefinition";
import SearchFrom3 from "@/components/inputs/SearchFrom3";
import CustomSelect from "@/components/inputs/CustomSelect";
import FiltersModal from "@/components/modals/FiltersModal";
import CustomTreeSelect from "@/components/inputs/CustomTreeSelect";

import FilterIcon from "@/icons/FilterIcon";
import CloseIcon from "@/icons/CloseIcon";

import { getObjectsOptions } from "@/api/objects";
import { getEmployeesOptions } from "@/api/employees";

import {
  getFullName,
  formateISODateToMoment,
  formateISODateToStr,
} from "@/utils";
import { formatMoney } from "@/utils/fieldValueConverter";

import {
  NUMBER_FIELDS,
  STRING_FIELDS,
  DEFINITIONS,
  DATE_FIELDS,
  MAP_FIELDS,
  UPLOAD_FIELDS,
  POINTS_FIELDS,
  OBJECTS_TYPES,
} from "@/constants/definitions";

const { Title } = Typography;

const NOT_FILLED_OPTION = {
  uuid: "notFilled",
  name: "Не заполнено",
  label: "Не заполнено",
};

const EMPTY_FILTER_GROUPS = [];
const SYSTEM_FILTER_GROUP = {
  uuid: "system_resource_dates",
  name: "Системные даты",
  filters: [
    {
      uuid: "created_at",
      field_name: "created_at",
      caption: "Дата создания",
      type: "moment",
      allowEmpty: false,
    },
    {
      uuid: "updated_at",
      field_name: "updated_at",
      caption: "Дата изменения",
      type: "moment",
      allowEmpty: false,
    },
  ],
};

const createFilterElement = (field, value, options, onChange) => {
  const { field_name, type, is_multiselect, allowEmpty = true } = field;

  let element;

  switch (true) {
    case type === "money":
      element = (
        <FilterMoney
          value={value}
          allowEmpty={allowEmpty}
          type={type}
          onSubmit={(val) => onChange(field_name, val)}
        />
      );
      break;
    case NUMBER_FIELDS.includes(type):
      element = (
        <FilterNumber
          value={value}
          allowEmpty={allowEmpty}
          type={type}
          onSubmit={(val) => onChange(field_name, val)}
        />
      );
      break;
    case STRING_FIELDS.includes(type):
      element = (
        <FilterString
          value={value}
          allowEmpty={allowEmpty}
          onSubmit={(val) => onChange(field_name, val)}
        />
      );
      break;
    case DATE_FIELDS.includes(type):
      element = (
        <FilterCalendar
          value={value}
          allowEmpty={allowEmpty}
          type={type}
          onSubmit={(val) => onChange(field_name, val)}
        />
      );
      break;

    case [...DEFINITIONS, "employee"].includes(type):
      element = (
        <FilterDefinition
          type={type}
          multi={is_multiselect}
          value={value}
          options={options}
          onSubmit={(val) => onChange(field_name, val)}
        />
      );
      break;
    case [...UPLOAD_FIELDS, ...MAP_FIELDS].includes(type):
      element = (
        <CustomSelect
          className="w-100"
          value={value}
          placeholder="Выбрать"
          options={options}
          onChange={(val) => onChange(field_name, val)}
          allowClear
          fieldNames={{ value: "uuid", label: "name" }}
          optionFilterProp="name"
        />
      );
      break;

    case POINTS_FIELDS.includes(type):
      return null;

    default:
      element = <div>{type}</div>;
      break;
  }

  return element || null;
};

// Фильтры для таблицы объектов
const Filters = ({
  initialTableParams,
  filters,
  filtersGroups = EMPTY_FILTER_GROUPS,
  unitsTree,
  units,
  user,
  isAdmins = false,
  isRepository = false,
  onChange,
  clearFilters,
}) => {
  // Окно с фильтрами
  const [isFiltersModalShow, setIsFiltersModalShow] = useState(false);
  const [tempValueFilters, setTempValueFilters] = useState(null);
  const [options, setOptions] = useState({}); // Опции для фильтров по ресурсам
  const [authorSelectOpen, setAuthorSelectOpen] = useState(false);

  const filtersGroupsWithSystem = useMemo(
    () => [SYSTEM_FILTER_GROUP, ...filtersGroups],
    [filtersGroups],
  );

  // Все фильтры без групп
  const allFilters = useMemo(() => {
    return filtersGroupsWithSystem.map(({ filters }) => filters || []).flat();
  }, [filtersGroupsWithSystem]);

  // Возвращает плоский список РГ
  const getFlatUnits = useMemo(() => {
    const flat = [];

    function traverse(nodes) {
      nodes.forEach((node) => {
        flat.push(node);
        if (Array.isArray(node.children) && node.children.length > 0) {
          traverse(node.children);
        }
      });
    }

    if (!unitsTree) return [];

    traverse(unitsTree);
    return flat;
  }, [unitsTree]);

  // Запрашиваем опции для фильтров по ресурсам
  useEffect(() => {
    const getOptions = async (defId) => {
      try {
        const { objects } = await getObjectsOptions(
          {
            definition_uuid: defId,
          },
          isAdmins,
        );

        return objects.map((item) => ({ ...item, label: item.name }));
      } catch (error) {
        console.log(error);
      }
    };

    const getEmployees = async () => {
      try {
        const { employees } = await getEmployeesOptions(
          { deleted: false },
          false, // isAdmins передать не можем, так как ответы у ручек не совпадают и используются для разных целей. Тут нужна обычная даже для админа
        );

        return employees.map((item) => ({
          ...item,
          label: getFullName(item.lastname, item.firstname, item.patronymic),
        }));
      } catch (error) {
        console.log(error);
      }
    };

    const fetchOptions = async () => {
      const employees = await getEmployees();

      setOptions((prev) => ({
        ...prev,
        author_uuid: employees || [],
      }));

      if (!allFilters.length) return;

      allFilters.forEach((filter) => {
        if (filter.type === "employee") {
          getEmployees().then((res) => {
            setOptions((prev) => ({
              ...prev,
              [filter.field_name]: [NOT_FILLED_OPTION, ...(res || [])],
            }));
          });
        } else if (DEFINITIONS.includes(filter.type)) {
          getOptions(filter.object).then((res) => {
            setOptions((prev) => ({
              ...prev,
              [filter.field_name]: [NOT_FILLED_OPTION, ...(res || [])],
            }));
          });
        } else if ([...UPLOAD_FIELDS, ...MAP_FIELDS].includes(filter.type)) {
          setOptions((prev) => ({
            ...prev,
            [filter.field_name]: [
              { uuid: "filled", name: "Заполнено" },
              NOT_FILLED_OPTION,
            ],
          }));
        }
      });
    };

    fetchOptions();
  }, [allFilters, isAdmins]);

  // Обработчик для открытия модалки с фильтрами
  const openFiltersModal = () => {
    setTempValueFilters({
      ...filters,
    });
    setIsFiltersModalShow(true);
  };

  // Обработчик для смены временных фильтров в модалке
  const handleTempFilterChange = (name, val) => {
    setTempValueFilters((prev) => ({
      ...prev,
      [name]: val || null,
    }));
  };

  // Применение выбранных фильтров из модалки
  const applyFilters = () => {
    onChange(tempValueFilters);
    setTempValueFilters(null);
    setIsFiltersModalShow(false);
  };

  const formatTagCaption = (str, type) => {
    if (str === null || str === undefined || str === "") return null;

    if (type === "money") {
      return formatMoney(str);
    }

    if (typeof str === "string") {
      if (str.endsWith("Z") || str.endsWith(".000Z")) {
        return formateISODateToMoment(str);
      }

      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        return formateISODateToStr(str);
      }
    }

    return str;
  };

  const selectedFilters = useMemo(() => {
    const selected = { ...filters };
    delete selected.name;
    delete selected.unit_uuid;
    delete selected.object_type;
    delete selected.units_uuids;

    return Object.entries(selected)
      .map(([key, value]) => {
        if (value === null) {
          return null;
        }

        const filter = allFilters.find((item) => item.field_name === key) || {
          caption: key === "author_uuid" ? "Автор" : key,
        };

        if (value === "notFilled") {
          return {
            key: key,
            uuid: key,
            label: `${filter?.caption}: Не заполнено`,
          };
        }

        if (typeof value === "object") {
          if (Array.isArray(value)) {
            const optionsList = options[key];

            if (!optionsList || !value.length) return null;
            return {
              key: key,
              uuid: key,
              label: [
                `${filter?.caption}:`,
                value
                  .map((v) => {
                    const res = optionsList.find(({ uuid }) => uuid === v);
                    return res ? res.name : v;
                  })
                  .join(", "),
              ].join(" "),
            };
          } else {
            return {
              key: key,
              uuid: key,
              label: [
                `${filter?.caption}:`,
                value.from &&
                  `от ${formatTagCaption(value.from, filter.type)}`,
                value.to &&
                  `до ${formatTagCaption(value.to, filter.type)}`,
              ].join(" "),
            };
          }
        } else {
          const optionsList = options[key];

          if (optionsList) {
            const res = optionsList.find(({ uuid }) => uuid === value);

            if (res) {
              return {
                key: key,
                uuid: key,
                label: `${filter?.caption}: ${res.name || res.label}`,
              };
            } else return null;
          } else {
            return {
              key: key,
              uuid: key,
              label: `${filter?.caption}: ${formatTagCaption(value, filter.type)}`,
            };
          }
        }
      })
      .filter(Boolean);
  }, [filters, options, allFilters]);

  const isAnyFilterApplied = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      const initialValue = initialTableParams.filters[key];

      if ((!initialValue && !value) || (Array.isArray(value) && !value.length))
        return false;

      if (Array.isArray(value)) {
        return JSON.stringify(value) !== JSON.stringify(initialValue);
      }

      return value !== initialValue;
    });
    // eslint-disable-next-line
  }, [filters]);

  const handleDeselectFilter = (value, option) => {
    onChange({ [value]: null });
  };

  return (
    <>
      <Row gutter={8}>
        <Col className={styles.filterColumn} xs={24} md={7} lg={7}>
          <SearchFrom3
            className={styles.searchInput}
            placeholder="Поиск"
            defaultValue={filters.name}
            onSearch={(search) => onChange({ name: search || null })}
          />
        </Col>
        <Col className={styles.filterColumn} xs={24} md={15} lg={17}>
          <Flex gap={8} align="center">
            {!isRepository && (
              <>
                {!isAdmins ? (
                  <>
                    <CustomTreeSelect
                      placeholder="Рабочая группа"
                      data={unitsTree}
                      value={filters.units_uuids}
                      onChange={(units_uuids) => {
                        onChange({
                          units_uuids: units_uuids,
                          object_type: "all",
                        });
                      }}
                      style={{
                        width: 180,
                      }}
                    />

                    <CustomSelect
                      placeholder="Тип"
                      options={OBJECTS_TYPES}
                      popupMatchSelectWidth={false}
                      value={filters.object_type}
                      onChange={(val) =>
                        onChange({
                          object_type: val,
                          units_uuids:
                            val === "all"
                              ? []
                              : val === "root"
                                ? getFlatUnits.filter(
                                    ({ readOnly }) => !readOnly,
                                  )
                                : getFlatUnits.filter(
                                    ({ readOnly }) => readOnly,
                                  ),
                        })
                      }
                      optionRender={(option) => (
                        <Flex gap={5} align="center">
                          {option?.data?.icon}
                          <span>{option.label}</span>
                        </Flex>
                      )}
                      labelRender={(option) => {
                        const item = OBJECTS_TYPES.find(
                          (item) => item.value === option.value,
                        );

                        return (
                          <Flex gap={5} align="center">
                            {item?.icon}
                            <span>{item.label}</span>
                          </Flex>
                        );
                      }}
                      style={{
                        width: 130,
                      }}
                    />
                  </>
                ) : (
                  <CustomSelect
                    className={styles.adminUnitSelect}
                    placeholder="Рабочая группа"
                    options={units}
                    fieldNames={{
                      label: "name",
                      value: "uuid",
                      key: "uuid",
                    }}
                    style={{
                      width: "150",
                    }}
                    showSearch
                    optionFilterProp="name"
                    allowClear
                    popupMatchSelectWidth={false}
                    value={filters.unit_uuid}
                    onChange={(unit) => onChange({ unit_uuid: unit || null })}
                  />
                )}
                <CustomSelect
                  open={authorSelectOpen}
                  onOpenChange={setAuthorSelectOpen}
                  placeholder="Автор"
                  className={styles.authorSelect}
                  value={filters.author_uuid}
                  options={options.author_uuid || []}
                  showSearch
                  allowClear
                  fieldNames={{ value: "uuid" }}
                  optionFilterProp="label"
                  popupMatchSelectWidth={false}
                  onChange={(val) => onChange({ author_uuid: val || null })}
                  popupRender={(menu) => (
                    <>
                      {menu}
                      {options?.author_uuid
                        ?.map((u) => u.uuid)
                        ?.includes(user.employee_uuid) && (
                        <>
                          <Divider style={{ margin: "8px 0" }} />
                          <Space style={{ padding: "0 8px 4px" }}>
                            <Typography.Link
                              onClick={() => {
                                onChange({ author_uuid: user.employee_uuid });
                                setAuthorSelectOpen(false);
                              }}
                            >
                              Я автор
                            </Typography.Link>
                          </Space>
                        </>
                      )}
                    </>
                  )}
                />
              </>
            )}

            {!!allFilters.length && (
              <Button
                type="primary"
                icon={<FilterIcon />}
                onClick={openFiltersModal}
              ></Button>
            )}

            {isAnyFilterApplied && !isRepository && (
              <Tooltip title="Очистить все фильтры" placement="bottom">
                <Button
                  color="default"
                  variant="link"
                  shape="circle"
                  size="small"
                  icon={<CloseIcon />}
                  onClick={clearFilters}
                  style={{ background: "#BBBBBB", color: "#fff" }}
                ></Button>
              </Tooltip>
            )}
          </Flex>
        </Col>
        <Col className={styles.filterColumn} xs={24}>
          {!!selectedFilters.length && (
            <CustomSelect
              mode="multiple"
              className="select__not-active w-100"
              value={selectedFilters.map((f) => f.uuid)}
              options={selectedFilters}
              fieldNames={{
                value: "uuid",
                key: "uuid",
              }}
              maxTagCount="responsive"
              popupRender={() => null}
              open={false}
              suffixIcon={false}
              showSearch={false}
              onDeselect={handleDeselectFilter}
            />
          )}
        </Col>
      </Row>

      {isFiltersModalShow && (
        <FiltersModal
          width={820}
          visible={isFiltersModalShow}
          onSave={applyFilters}
          onCancel={() => {
            setIsFiltersModalShow(false);
          }}
          onClear={() => {
            setIsFiltersModalShow(false);
            clearFilters();
          }}
        >
          <Collapse
            className={styles.filtersCollapse}
            bordered={false}
            items={filtersGroupsWithSystem
              .filter(({ filters }) => !!filters && filters.length)
              .map((group) => {
                return {
                  key: group.uuid,
                  label: (
                    <Title level={5} className="no-margin">
                      {group.name}
                    </Title>
                  ),
                  children: (
                    <Descriptions
                      className={styles.modalDescriptions}
                      column={1}
                      colon={false}
                    >
                      {group.filters
                        .filter(({ type }) => !POINTS_FIELDS.includes(type))
                        .map((filter) => {
                          return (
                            <Descriptions.Item
                              label={filter.caption}
                              key={filter.uuid}
                            >
                              {createFilterElement(
                                filter,
                                tempValueFilters[filter.field_name],
                                options[filter.field_name],
                                handleTempFilterChange,
                              )}
                            </Descriptions.Item>
                          );
                        })}
                    </Descriptions>
                  ),
                };
              })}
            defaultActiveKey={[filtersGroupsWithSystem[0].uuid]}
          />
        </FiltersModal>
      )}
    </>
  );
};

export default Filters;
